// src/components/publishing/publication-history-page.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  History, FileCode2, Globe, Linkedin, ExternalLink,
  Loader2, AlertTriangle, CheckCircle2, Filter,
} from "lucide-react";
import { toast } from "sonner";
import {
  publishingApi, Destination, destinationLabel,
  HistoryItem,
} from "@/app/services/publishing/publishing-api";

interface PublicationHistoryPageProps {
  /** Currently active brand. History is filtered per-brand. */
  activeBrandName?: string;
}

export function PublicationHistoryPage({ activeBrandName }: PublicationHistoryPageProps) {
  // Alias so the existing `brandName` references below keep working.
  const brandName = activeBrandName;
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Destination | "all">("all");

  const load = async () => {
    setLoading(true);
    try {
      const list = await publishingApi.listHistory({
        brand_name: brandName,
        destination: filter === "all" ? undefined : filter,
        limit: 100,
      });
      setItems(list);
    } catch (e: any) {
      toast.error(e.message || "Could not load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandName, filter]);

  return (
    <div
      style={{
        minHeight: "100%",
        display: "flex", flexDirection: "column",
        backgroundColor: "var(--background)",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "20px 24px 16px",
          borderBottom: "1px solid var(--border)",
          backgroundColor: "var(--background)",
          position: "sticky", top: 0, zIndex: 5,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <div
            style={{
              width: 32, height: 32, borderRadius: 8,
              backgroundColor: "rgba(13,148,136,0.1)",
              border: "1px solid rgba(13,148,136,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <History style={{ width: 16, height: 16, color: "#0D9488" }} />
          </div>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: "var(--foreground)", margin: 0 }}>
              Publication history
            </h1>
            <p style={{ fontSize: 12, color: "var(--muted-foreground)", margin: 0 }}>
              Every post published from Upmode, by destination.
            </p>
          </div>
        </div>

        <FilterBar value={filter} onChange={setFilter} />
      </div>

      {/* Body */}
      <div style={{ flex: 1, padding: "16px 24px 40px" }}>
        {loading && items.length === 0 && (
          <div style={{
            padding: 40, textAlign: "center",
            color: "var(--muted-foreground)", fontSize: 13,
          }}>
            <Loader2 style={{ width: 20, height: 20 }} className="animate-spin" />
          </div>
        )}

        {!loading && items.length === 0 && <EmptyState />}

        {items.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {items.map(item => (
              <HistoryRow key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Filter bar ──────────────────────────────────────────────────────────────

function FilterBar({
  value, onChange,
}: {
  value: Destination | "all";
  onChange: (v: Destination | "all") => void;
}) {
  const options: Array<{ id: Destination | "all"; label: string }> = [
    { id: "all",       label: "All" },
    { id: "hashnode",  label: "Hashnode" },
    { id: "wordpress", label: "WordPress" },
    { id: "linkedin",  label: "LinkedIn" },
  ];

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
      <Filter style={{ width: 13, height: 13, color: "var(--muted-foreground)" }} />
      {options.map(o => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            style={{
              padding: "5px 11px", borderRadius: 999,
              border: `1px solid ${active ? "#0D9488" : "var(--border)"}`,
              backgroundColor: active ? "rgba(13,148,136,0.08)" : "var(--muted)",
              color: active ? "#0D9488" : "var(--muted-foreground)",
              fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div
      style={{
        padding: 50, textAlign: "center", borderRadius: 12,
        border: "1px dashed var(--border)", backgroundColor: "var(--muted)",
      }}
    >
      <History
        style={{
          width: 32, height: 32, color: "var(--muted-foreground)",
          marginBottom: 10, opacity: 0.5,
        }}
      />
      <h3 style={{ fontSize: 14, fontWeight: 700, color: "var(--foreground)", margin: "0 0 4px 0" }}>
        No posts published yet
      </h3>
      <p style={{ fontSize: 12, color: "var(--muted-foreground)", margin: 0 }}>
        Publish from Brand Publisher to see history here.
      </p>
    </div>
  );
}

// ─── History row ─────────────────────────────────────────────────────────────

function HistoryRow({ item }: { item: HistoryItem }) {
  const meta = {
    hashnode:  { Icon: FileCode2, color: "#2962FF" },
    wordpress: { Icon: Globe,     color: "#21759B" },
    linkedin:  { Icon: Linkedin,  color: "#0A66C2" },
  }[item.destination];

  const snapshot = item.content_snapshot || {};
  const title = useMemo(() => {
    if (typeof snapshot.title === "string" && snapshot.title.trim()) {
      return snapshot.title.trim();
    }
    if (typeof snapshot.text === "string" && snapshot.text.trim()) {
      const firstLine = snapshot.text.trim().split("\n")[0];
      return firstLine.slice(0, 120) + (firstLine.length > 120 ? "…" : "");
    }
    return "(untitled)";
  }, [snapshot]);

  const succeeded = item.status === "published" || item.status === "sent";

  return (
    <div
      style={{
        display: "flex", alignItems: "flex-start", gap: 10,
        padding: "12px 14px", borderRadius: 10,
        border: "1px solid var(--border)",
        backgroundColor: "var(--background)",
      }}
    >
      <div
        style={{
          width: 32, height: 32, borderRadius: 8,
          backgroundColor: "var(--muted)", border: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <meta.Icon style={{ width: 15, height: 15, color: meta.color }} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
          marginBottom: 2,
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--foreground)" }}>
            {destinationLabel(item.destination)}
          </span>
          {succeeded ? (
            <span style={chipSt("rgba(16,185,129,0.1)", "#10B981", "rgba(16,185,129,0.25)")}>
              <CheckCircle2 style={{ width: 10, height: 10 }} />
              Published
            </span>
          ) : (
            <span style={chipSt("rgba(239,68,68,0.08)", "#EF4444", "rgba(239,68,68,0.25)")}>
              <AlertTriangle style={{ width: 10, height: 10 }} />
              Failed
            </span>
          )}
          <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
            · {formatDate(item.published_at)}
          </span>
        </div>

        <div style={{
          fontSize: 13, color: "var(--foreground)", lineHeight: 1.45,
          overflowWrap: "anywhere", wordBreak: "break-word",
        }}>
          {title}
        </div>

        {!succeeded && item.error_message && (
          <div style={{
            marginTop: 4, fontSize: 11, color: "#EF4444",
            overflowWrap: "anywhere", wordBreak: "break-word",
          }}>
            {item.error_message}
          </div>
        )}

        {item.external_id && (
          <div style={{
            marginTop: 4, fontSize: 11, color: "var(--muted-foreground)",
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
          }}>
            ID: {item.external_id}
          </div>
        )}
      </div>

      {item.external_url && (
        <a
          href={item.external_url}
          target="_blank"
          rel="noreferrer"
          style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "6px 10px", borderRadius: 7,
            border: "1px solid var(--border)",
            backgroundColor: "var(--background)",
            color: "#0D9488",
            fontSize: 12, fontWeight: 600, textDecoration: "none",
            flexShrink: 0,
          }}
        >
          View
          <ExternalLink style={{ width: 11, height: 11 }} />
        </a>
      )}
    </div>
  );
}

function chipSt(bg: string, color: string, border: string): React.CSSProperties {
  return {
    display: "inline-flex", alignItems: "center", gap: 3,
    padding: "1px 6px", borderRadius: 999,
    backgroundColor: bg, color, border: `1px solid ${border}`,
    fontSize: 10, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.04em",
  };
}

function formatDate(iso: string): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours < 1) return "just now";
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}